import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { businessInfo, operatorDisclosure } from '../utils/businessInfo';

const stats = [
    { number: 'Live', label: 'Android App' },
    { number: 'Soon', label: 'iOS App' },
    { number: 'UGC', label: 'Creator Tools' },
    { number: '24/7', label: 'Safety Support' },
];

const features = [
    {
        icon: '🎥',
        title: 'Create & Share',
        description: 'Easy-to-use tools for creating stunning short videos and going live with your audience.',
    },
    {
        icon: '🌐',
        title: 'Supported Regions',
        description: 'Connect with users and discover diverse content across supported regions.',
    },
    {
        icon: '⭐',
        title: 'Creator Features',
        description: 'Explore creator reward programs and virtual gifts where those features are currently available.',
    },
    {
        icon: '🛡️',
        title: 'Safe Environment',
        description: 'Advanced moderation and safety features to ensure a positive experience for all users.',
    },
];

const values = [
    { title: 'Creativity First', description: 'We put creators at the center of everything we do' },
    { title: 'Safety & Respect', description: 'Building a safe, inclusive community for everyone' },
    { title: 'Innovation', description: 'Continuously improving with cutting-edge technology' },
    { title: 'Transparency', description: 'Clear policies and open communication with our community' },
    { title: 'Community', description: 'Celebrating creativity while bringing people together' },
];

export const AboutPage: React.FC = () => {
    return (
        <Layout>
            <SEO
                title="About Swag Shots - Create. Connect. Earn."
                description="Learn more about Swag Shots, a short-video and live-streaming app that helps creators share content and connect with audiences."
                url="https://www.swagshots.com/about"
            />

            {/* Hero Section */}
            <section className="bg-gradient-to-br from-primary-600 to-primary-500 text-white pt-24 pb-16">
                <div className="container-custom">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">About Swag Shots</h1>
                        <p className="text-xl text-primary-100 mb-12 max-w-3xl mx-auto">
                            Helping creators share short videos, go live, and connect with audiences
                        </p>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                            {stats.map((stat, index) => (
                                <div
                                    key={index}
                                    className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20"
                                >
                                    <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                                        {stat.number}
                                    </div>
                                    <div className="text-primary-100 font-medium">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div className="container-custom py-16">
                <div className="max-w-4xl mx-auto">
                    {/* Mission Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            At Swag Shots, we believe that everyone has a story to tell and talent to share. Our mission is to
                            create a platform where creativity can grow, creators can connect with audiences,
                            and supported creator features can reward participation where available.
                        </p>
                    </section>

                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Business & Registration</h2>
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-3">
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {operatorDisclosure}
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold text-gray-900">Operator:</span> {businessInfo.operatorName}
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold text-gray-900">GSTIN:</span> {businessInfo.gstin}
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold text-gray-900">Registered Address:</span> {businessInfo.registeredAddress}
                            </p>
                        </div>
                    </section>

                    {/* Features Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">What Makes Swag Shots Special</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 text-center hover:shadow-xl transition-shadow"
                                >
                                    <div className="text-4xl mb-4">{feature.icon}</div>
                                    <h3 className="text-xl font-semibold text-primary-600 mb-3">{feature.title}</h3>
                                    <p className="text-gray-600">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Values Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Values</h2>
                        <div className="space-y-6">
                            {values.map((value, index) => (
                                <div key={index} className="flex items-start space-x-4">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mt-3 flex-shrink-0"></div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">{value.title}:</h3>
                                        <p className="text-gray-700">{value.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Join Community Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Join Our Community</h2>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            Whether you're a seasoned content creator or just starting your journey, Swag Shots provides the tools,
                            audience, and opportunities you need to start creating. Join creators who are already sharing
                            their passion on Swag Shots.
                        </p>
                    </section>
                </div>
            </div>

            {/* CTA Section */}
            <section className="bg-gray-50 py-16">
                <div className="container-custom text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Start Creating?</h2>
                    <p className="text-lg text-gray-600 mb-8">
                        Download Swag Shots for Android today. iOS availability is coming soon.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/#download">
                            <Button variant="primary" size="lg">
                                Download Now
                            </Button>
                        </Link>
                        <Link to="/">
                            <Button variant="secondary" size="lg">
                                Learn More
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
};
